/**
 * The specialist's view of one test case — its JSON file, validated on save.
 * Opened from a case card (Edit), from a run result ("The test is wrong") and,
 * with `initial`, for a brand-new case ("Write it myself").
 */

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import CodeMirror from '@uiw/react-codemirror'
import { json } from '@codemirror/lang-json'
import { oneDark } from '@codemirror/theme-one-dark'
import { Save, Trash2 } from 'lucide-react'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useThemeStore } from '@/store/themeStore'
import { apiError, apiIssues, deleteCase, getCase, saveCase, type Issue, type TestCase } from '@/api/uiTesting'
import { IssuesTable, mutedText } from './shared'

export default function CaseEditor({ project, caseId, initial, canTest, onClose, onChanged }: {
  project: string
  caseId: string
  /** A case not saved yet — shown as-is instead of being loaded. */
  initial?: TestCase
  canTest: boolean
  onClose: () => void
  onChanged: () => Promise<void> | void
}) {
  const { theme } = useThemeStore()
  const { confirm, confirmDialog } = useConfirm()
  const isNew = !!initial
  const [text, setText] = useState(initial ? JSON.stringify(initial, null, 2) : '')
  const [origin, setOrigin] = useState<string>(initial ? String(initial.origin || 'user') : '')
  const [issues, setIssues] = useState<Issue[]>([])
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(!initial)

  useEffect(() => {
    if (initial) return
    let alive = true
    getCase(project, caseId)
      .then((c) => {
        if (!alive) return
        setText(JSON.stringify(c, null, 2))
        setOrigin(String(c.origin || 'agent'))
      })
      .catch((err) => toast.error(apiError(err, 'Could not load the case')))
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [project, caseId, initial])

  const onSave = async () => {
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      toast.error(`The case is not valid JSON: ${(e as Error).message}`)
      return
    }
    const id = String(parsed.id ?? '')
    if (!id) { toast.error('The case needs an "id"'); return }
    setBusy(true)
    setIssues([])
    try {
      const r = await saveCase(project, id, { ...parsed, id })
      toast.success(r.warnings.length ? `Saved with ${r.warnings.length} warning(s)` : 'Case saved')
      setIssues(r.warnings)
      await onChanged()
      if (isNew && !r.warnings.length) onClose()
    } catch (err) {
      setIssues(apiIssues(err))
      toast.error(apiError(err, 'Could not save the case'))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async () => {
    if (!(await confirm({ title: 'Delete case', message: `Delete ${caseId}? This removes its file from the project.` }))) return
    setBusy(true)
    try {
      await deleteCase(project, caseId)
      toast.success(`${caseId} deleted`)
      await onChanged()
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Could not delete the case'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={isNew ? 'New test case' : caseId} size="full" maximizable resizable
           storageKey="ui-testing-case-editor" footer={canTest ? (
      <>
        {!isNew && <Button variant="danger" icon={<Trash2 size={14} />} onClick={onDelete} disabled={busy}>Delete</Button>}
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button icon={<Save size={14} />} loading={busy} onClick={onSave} disabled={loading}>Save</Button>
      </>
    ) : <Button variant="secondary" onClick={onClose}>Close</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}>
        {isNew && (
          <div style={{ ...mutedText, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}>
            Fill in the title and the steps. Each step is either a <b>do</b> (open_screen, fill, select,
            click_action…) or an <b>expect</b> (message, record, field_state…); the case is checked against
            the application&apos;s screens when you save.
          </div>
        )}
        {origin === 'seed' && (
          <div style={{ ...mutedText, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}>
            This case was generated from the models. Once you save an edit it becomes origin
            {' '}<b>user</b>, and re-generating the baseline never overwrites it.
          </div>
        )}
        <div style={{ flex: 1, minHeight: 300, border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
          {loading ? <div style={{ ...mutedText, padding: 12 }}>Loading…</div> : (
            <CodeMirror value={text} onChange={setText} extensions={[json()]}
                        theme={theme === 'dark' ? oneDark : undefined} height="100%"
                        style={{ height: '100%' }} editable={canTest}
                        basicSetup={{ lineNumbers: true, foldGutter: true }} />
          )}
        </div>
        {issues.length > 0 && <IssuesTable issues={issues} />}
      </div>
      {confirmDialog}
    </Modal>
  )
}

/** A starting point for "Write it myself": valid shape, the rule it proves. */
export function newCaseTemplate(opts: { object?: string; ruleId?: string; ruleText?: string }): TestCase {
  const tag = (s: string) => s.toUpperCase().replace(/[^A-Z0-9_.-]+/g, '_').replace(/^_+|_+$/g, '')
  const parts = [tag(opts.object || 'APP') || 'APP', tag(opts.ruleId || 'NEW') || 'NEW']
  const id = `TC-${parts.join('-')}`.slice(0, 62)
  return {
    id,
    title: opts.ruleText ? `${opts.ruleText}` : '',
    object: opts.object || '',
    surface: 'app',
    type: 'negative',
    priority: 'medium',
    persona: 'owner',
    origin: 'user',
    requirement: opts.ruleId ? [opts.ruleId] : [],
    steps: opts.object
      ? [{ do: 'open_screen', obj: opts.object }, { do: 'click_action', name: 'add' }]
      : [],
  }
}
